import { useKeycloak } from '@react-keycloak/web'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import { useAppContext } from '@/hooks'
import { useTaskMonitor } from '@/hooks/useTaskMonitor'
import { ConfirmationOptions } from '@/pages/App/types'
import { MassSqlExportModal } from '@/pages/User/pages/Home/components/TreePanel/components/MassSqlExportModal'
import { useCancelTaskUsingDeleteMutation, useCreateReleaseScriptsMutation } from '@/rtkApi'
import {
	MassExportExternalConfigDto,
	useCreateDeploymentsMutation,
} from '@/rtkApi/mddApiSliceGeneratedV2'
import { downloadTaskResultFile } from '@/utils/downloadTaskResultFile'
import { getApiErrorDescription, logError, logErrorAndNotifyWithToast } from '@/utils/errorHandling'

import { DdlModalProps } from '../../types'
import { GenerateScriptsConfigDialog } from './GenerateScriptsConfigDialog'
import { ScriptGenerationPhase } from './scriptGenerationActions'
import { ScriptGenerationDialog } from './ScriptGenerationDialog'

export const DdlModal = ({
	node,
	deploymentPackage,
	env,
	preview,
	onClose,
	type,
}: DdlModalProps) => {
	const { t } = useAppContext()
	const { keycloak } = useKeycloak()
	const { monitorTask, stopMonitoring } = useTaskMonitor()
	const [createReleaseScripts] = useCreateReleaseScriptsMutation()
	const [createDeployments] = useCreateDeploymentsMutation()
	const [cancelTask] = useCancelTaskUsingDeleteMutation()

	const [phase, setPhase] = useState<ScriptGenerationPhase>('config')
	const [progress, setProgress] = useState(0)
	const [description, setDescription] = useState('')
	const [errorMessage, setErrorMessage] = useState<string | null>(null)
	const [config, setConfig] = useState<MassExportExternalConfigDto | undefined>()
	const scriptTaskIdRef = useRef<number | null>(null)
	const activeTaskIdRef = useRef<number | null>(null)
	const ignoreRef = useRef(false)

	useEffect(() => stopMonitoring, [stopMonitoring])

	const trackProgress = async (options: ConfirmationOptions) => {
		if (ignoreRef.current) return null
		setProgress(options.progress ?? 0)
		if (typeof options.description === 'string') setDescription(options.description)
		return null
	}

	const showFailure = (error: unknown, detail?: string) => {
		if (ignoreRef.current) return
		setPhase('error')
		setErrorMessage(detail || getApiErrorDescription(error))
	}

	const onExport = async (exportConfig: MassExportExternalConfigDto) => {
		const massExportExternalConfigDto: MassExportExternalConfigDto = {
			...exportConfig,
			environmentCode: env.code,
		}

		ignoreRef.current = false
		scriptTaskIdRef.current = null
		activeTaskIdRef.current = null
		setConfig(massExportExternalConfigDto)
		setPhase('generating')
		setProgress(0)
		setErrorMessage(null)
		setDescription(t('TASK_RUNNING_WAIT'))

		try {
			const task = await createReleaseScripts({
				dpId: deploymentPackage.id!,
				envCode: env.code!,
				massExportExternalConfigDto,
			}).unwrap()

			if (!task?.id) {
				showFailure(new Error(t('TASK_ERROR')))
				return
			}

			scriptTaskIdRef.current = task.id
			activeTaskIdRef.current = task.id

			await monitorTask(task.id, {
				onError: (error, detail) => showFailure(error, detail),
				onProgressUpdate: trackProgress,
				onSuccess: async () => {
					if (ignoreRef.current) return
					setProgress(100)
					setDescription(t('TASK_FINISHED_DESC'))
					setPhase('ready')
				},
			})
		} catch (error) {
			showFailure(error)
		}
	}

	const onDeploy = async () => {
		if (!config) return
		setPhase('deploying')
		setProgress(0)
		setErrorMessage(null)
		setDescription(t('TASK_RUNNING_WAIT'))

		try {
			const task = await createDeployments({
				dpId: deploymentPackage.id!,
				envCode: env.code!,
				massExportExternalConfigDto: config,
			}).unwrap()

			if (!task?.id) {
				toast(t('TASK_ERROR'), { type: 'error' })
				setPhase('ready')
				return
			}

			activeTaskIdRef.current = task.id
			await monitorTask(task.id, {
				onError: (error, detail) => {
					if (ignoreRef.current) return
					setPhase('ready')
					setProgress(100)
					setDescription(t('TASK_FINISHED_DESC'))
					setErrorMessage(detail || getApiErrorDescription(error))
				},
				onProgressUpdate: trackProgress,
				onSuccess: async () => {
					if (ignoreRef.current) return
					setProgress(100)
					setDescription(t('DEPLOYMENT_MARK_CREATED'))
					setErrorMessage(null)
					setPhase('deployed')
				},
			})
		} catch (error) {
			if (ignoreRef.current) return
			setPhase('ready')
			setProgress(100)
			setDescription(t('TASK_FINISHED_DESC'))
			setErrorMessage(getApiErrorDescription(error))
		}
	}

	const onDownload = async () => {
		const taskId = scriptTaskIdRef.current
		const token = keycloak.token
		if (taskId == null || !token) {
			toast(t('TASK_ERROR'), { type: 'error' })
			return
		}

		try {
			const fileName = config?.massExportStrategy === 'SINGLE_FILE' ? 'export.txt' : 'export.zip'
			await downloadTaskResultFile(taskId, token, fileName)
		} catch (error) {
			logErrorAndNotifyWithToast({
				context: { operation: 'DdlModal.onDownload' },
				detail: getApiErrorDescription(error),
				error,
				messageKey: 'TASK_EXECUTION_FAILED',
				t,
			})
		}
	}

	const onCancel = async () => {
		ignoreRef.current = true
		stopMonitoring()
		const taskId = activeTaskIdRef.current
		if (taskId != null) {
			try {
				await cancelTask({ id: taskId }).unwrap()
			} catch (error) {
				logError(error, {
					details: { taskId },
					operation: 'DdlModal.onCancel',
				})
			}
		}
		onClose()
	}

	return (
		<>
			{preview ? (
				<MassSqlExportModal
					node={node}
					env={env}
					open
					onClose={onClose}
					enableExportStrategy={false}
					releaseId={deploymentPackage.id}
					type={type}
				/>
			) : null}
			{!preview && phase === 'config' ? (
				<GenerateScriptsConfigDialog
					deploymentPackageId={deploymentPackage.id!}
					envName={env.name}
					structureId={node.id}
					onClose={onClose}
					onGenerate={(exportConfig) => void onExport(exportConfig)}
				/>
			) : null}
			{phase !== 'config' ? (
				<ScriptGenerationDialog
					description={description}
					envName={env.name}
					errorMessage={errorMessage}
					onCancel={() => void onCancel()}
					onClose={onClose}
					onDeploy={() => void onDeploy()}
					onDownload={() => void onDownload()}
					phase={phase}
					progress={progress}
				/>
			) : null}
		</>
	)
}
